// @ts-strict

import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { countries } from '@app/core/helpers/countries';
import { calculatePercentChange } from '@app/core/helpers/tools';
import { ShareOpenModalStatusService } from '@services/share-open-modal-status/share-open-modal-status.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SingleCampaignModalComponent } from '@app/shared/components/modals/single-campaign-modal.component';
import { StatisticsSummaryService } from '@services/statistics/statistics-summary.service';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { UAParser } from 'ua-parser-js';
import { AvailableStatisticsTableColumns, TableService } from '../../table.service';

const leadsStatuses = {
  '-1': 'any',
  0: 'toPay',
  1: 'pending',
  2: 'rejected',
  3: 'accepted'
};

interface Type {
  id: number;
  system_name: string;
}

interface Country {
  id: number;
  code: string;
}

interface Category {
  id: number;
  children: any[];
}

interface ContentItemCampaign {
  program_id: number;
  program_name: string;
  admin_program_id: number;
  admin_program_name: string;
  status: string;
  program_logo: string;
  visits: string;
  leads: string;
  leads_status_payment_waiting: string;
  leads_status_wait: string;
  leads_status_rejected: string;
  earnings_status_to_payment: string;
  earnings_status_payment_waiting: string;
  earnings_status_wait: string;
  earnings_status_rejected: string;
  conversion: string;
  epc: string;
  acceptance: string;
  currency: string;
  types: Type[];
  rate: string;
  countries: Country[];
  categories: Category[];
  traffic_sources: any[];
  created_at: string;
  updated_at: string;
  user_agent: any;
  day: string;
  avatar: string;
  username: string;
  country: any;
  id: number;
  config_name: string;
  config_id: number;
  amount: number;
  transaction_id: number;
  order_id: number;
  additional_data: {
    deposit: string | null;
    ngr: string | null;
    turnover: string | null;
  } | null;
  cart_value: {
    amount_original_currency: string;
    amount_user_currency: string;
  } | null;
  user_transaction_parameters: any;
  referrer: string;
  promotion_place: string;
  safe_page: string;
  user_agent_details: any;
  registration_date: string;
}

@Component({
  selector: '[table-item]',
  templateUrl: './content-item.component.html',
  styleUrls: ['./content-item.component.scss']
})
export class ContentItemComponent implements OnInit, OnChanges {
  @Input() compareMode = false;
  @Input() campaign?: ContentItemCampaign;
  @Input() title = '';
  @Input() paramToCompare = '';
  @Input() statisticsCompareData: any;
  @Input() datesDifference = 0;
  @Input() compareToCurrentDatesDifference = 0;
  @Input() index = 0;

  public maxFlags = 3;
  public toPayPercent = 0;
  public acceptedPercent = 0;
  public pendingPercent = 0;
  public rejectedPercent = 0;
  public toPayComparePercent = 0;
  public acceptedComparePercent = 0;
  public pendingComparePercent = 0;
  public rejectedComparePercent = 0;
  public icons = ['money-bag-alt', 'money-protection', 'money-time', 'cross-circle'];

  public campaignToCompare: any;
  public campaignCountryList: any;
  public leadPercentChange?: number | string;
  public visitsPercentChange?: number | string;
  public conversionPercentChange?: number | string;
  public acceptancePercentChange?: number | string;
  public epcPercentChange?: number | string;
  public ratePercentChange?: number;
  public earnPercentChange?: number | string;
  public salesPercentChange?: number | string;
  public Object = Object;
  public countriesList = countries;
  public parsedUserAgent: any;
  public leadsStatuses = leadsStatuses;
  public earnsToDisplay: any;
  public compareEarnsToDisplay = 0;
  public summaryOfEarns = 0;
  public compareSummaryOfEarns = 0;
  public leadsSummary = 0;
  public compareLeadsSummary = 0;

  public categoriesToDisplay: number[] = [];
  public maxShownCategories = 2;

  public isDetailsTableLayout = false;

  public earningsStatusPaymentWaiting = 0;
  public earningsStatusToPayment = 0;
  public earningsStatusRejected = 0;
  public earningsStatusWait = 0;
  public earningsStatusPaymentWaitingCompare = 0;
  public earningsStatusToPaymentCompare = 0;
  public earningsStatusRejectedCompare = 0;
  public earningsStatusWaitCompare = 0;

  public showMlSubs = false;

  public activeColumnsNames$!: Observable<AvailableStatisticsTableColumns[]>;

  private destroy$ = new Subject<void>();

  constructor(
    private readonly statisticsService: StatisticsSummaryService,
    private readonly modalService: NgbModal,
    private readonly shareOpenModalStatus: ShareOpenModalStatusService,
    private readonly statisticsTableService: TableService
  ) {}

  ngOnInit(): void {
    if (this.title === 'leads' || this.title === 'visits') {
      this.compareMode = false;
    }
    if (this.campaign && typeof this.campaign.user_agent !== 'object') {
      this.parsedUserAgent = UAParser(this.campaign.user_agent);
    }
    this.getDataForEarningsBar();
    this.getLeadsSummary();

    this.statisticsService.isDetailsLayout
      .pipe(
        tap(isDetailsTableLayout => (this.isDetailsTableLayout = isDetailsTableLayout)),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.activeColumnsNames$ = this.statisticsTableService.tableColumns$.pipe(
      map(activeColumns => {
        return activeColumns
          .map(({ results }) => results)
          .flat()
          .filter(({ checked }) => checked)
          .map(({ column }) => column);
      })
    );

    if (this.campaign?.categories) {
      this.prepareCategoriesArray();
    }
    this.campaignCountryList = this.campaign?.countries ?? [];

    // this.showMlSubs = Object.values(this.campaign?.user_transaction_parameters ?? {}).every(x => x !== null);
    this.showMlSubs = !Object.values(this.campaign?.user_transaction_parameters ?? {}).every(x => x === null);
  }

  ngOnChanges(): void {
    if (this.title !== 'leads' && this.title !== 'visits') {
      if (this.compareMode) {
        if (this.statisticsCompareData.length) {
          this.setCompareCampaign(this.title);

          if (this.campaignToCompare) {
            if (!this.leadsSummary) {
              this.getLeadsSummary();
            }
            if (!this.summaryOfEarns) {
              this.getDataForEarningsBar();
            }
            this.getDataToDisplayComparison();
          } else {
            this.campaignToCompare = {};
          }
        } else {
          this.campaignToCompare = {};
        }
      }
    }
  }

  public checkIfColumnActive = (columnName: AvailableStatisticsTableColumns, activeColumns: AvailableStatisticsTableColumns[]) => {
    return activeColumns.includes(columnName);
  };

  prepareCategoriesArray(): void {
    this.campaign?.categories.map(category => {
      this.categoriesToDisplay.push(category.id);
      if (category.children && category.children.length) {
        category.children.map(child => {
          this.categoriesToDisplay.push(child.id);
          if (child.children && child.children.length) {
            child.children.map((grandchild: any) => {
              this.categoriesToDisplay.push(grandchild.id);
            });
          }
        });
      }
    });
  }

  expandOrCollapseCategories(): void {
    if (this.maxShownCategories === 2) {
      this.maxShownCategories = this.categoriesToDisplay.length;
    } else {
      this.maxShownCategories = 2;
    }
  }

  getLeadsSummary(): void {
    if (this.campaign) {
      this.leadsSummary =
        parseInt(this.campaign.leads, 10) + parseInt(this.campaign.leads_status_payment_waiting, 10) + parseInt(this.campaign.leads_status_wait, 10);
    }

    if (this.title === 'ivr' || this.title === 'hidelink') {
      this.leadsSummary = parseInt(this.campaign?.leads ?? '0', 10);
    }
  }

  getDataForEarningsBar(): void {
    if (this.campaign) {
      this.earningsStatusPaymentWaiting = parseFloat(this.campaign.earnings_status_payment_waiting);
      this.earningsStatusToPayment = parseFloat(this.campaign.earnings_status_to_payment);
      this.earningsStatusRejected = parseFloat(this.campaign.earnings_status_rejected);
      this.earningsStatusWait = parseFloat(this.campaign.earnings_status_wait);

      this.summaryOfEarns =
        parseFloat(this.campaign.earnings_status_payment_waiting) +
        parseFloat(this.campaign.earnings_status_to_payment) +
        parseFloat(this.campaign.earnings_status_rejected) +
        parseFloat(this.campaign.earnings_status_wait);
      this.earnsToDisplay =
        parseFloat(this.campaign.earnings_status_payment_waiting) +
        parseFloat(this.campaign.earnings_status_to_payment) +
        parseFloat(this.campaign.earnings_status_wait);
      this.toPayPercent = (parseFloat(this.campaign.earnings_status_to_payment) / this.summaryOfEarns) * 100;
      this.acceptedPercent = (parseFloat(this.campaign.earnings_status_payment_waiting) / this.summaryOfEarns) * 100;
      this.pendingPercent = (parseFloat(this.campaign.earnings_status_wait) / this.summaryOfEarns) * 100;
      this.rejectedPercent = (parseFloat(this.campaign.earnings_status_rejected) / this.summaryOfEarns) * 100;
    }
  }

  toFixed = (number: number | string, fractionDigits = 2) => {
    if (typeof number === 'string') {
      return number;
    }

    return number.toFixed(fractionDigits);
  };

  getDataToDisplayComparison(): void {
    this.getDataForCompareEarningsBar();
    this.calculateLeadsCompare();

    if (this.campaign) {
      this.visitsPercentChange = calculatePercentChange(parseInt(this.campaignToCompare.visits, 10), parseInt(this.campaign.visits, 10));
      this.acceptancePercentChange = calculatePercentChange(parseFloat(this.campaignToCompare.acceptance), parseFloat(this.campaign.acceptance));
      this.conversionPercentChange = calculatePercentChange(parseFloat(this.campaignToCompare.conversion), parseFloat(this.campaign.conversion));
      this.epcPercentChange = calculatePercentChange(parseFloat(this.campaignToCompare.epc), parseFloat(this.campaign.epc));
    }
  }

  getDataForCompareEarningsBar(): void {
    this.earningsStatusPaymentWaitingCompare = parseFloat(this.campaignToCompare.earnings_status_payment_waiting);
    this.earningsStatusToPaymentCompare = parseFloat(this.campaignToCompare.earnings_status_to_payment);
    this.earningsStatusRejectedCompare = parseFloat(this.campaignToCompare.earnings_status_rejected);
    this.earningsStatusWaitCompare = parseFloat(this.campaignToCompare.earnings_status_wait);

    this.compareSummaryOfEarns =
      parseFloat(this.campaignToCompare.earnings_status_payment_waiting) +
      parseFloat(this.campaignToCompare.earnings_status_to_payment) +
      parseFloat(this.campaignToCompare.earnings_status_rejected) +
      parseFloat(this.campaignToCompare.earnings_status_wait);

    this.compareEarnsToDisplay =
      parseFloat(this.campaignToCompare.earnings_status_payment_waiting) +
      parseFloat(this.campaignToCompare.earnings_status_to_payment) +
      parseFloat(this.campaignToCompare.earnings_status_wait);

    this.toPayComparePercent = (parseFloat(this.campaignToCompare.earnings_status_to_payment) / this.compareSummaryOfEarns) * 100;
    this.acceptedComparePercent = (parseFloat(this.campaignToCompare.earnings_status_payment_waiting) / this.compareSummaryOfEarns) * 100;
    this.pendingComparePercent = (parseFloat(this.campaignToCompare.earnings_status_wait) / this.compareSummaryOfEarns) * 100;
    this.rejectedComparePercent = (parseFloat(this.campaignToCompare.earnings_status_rejected) / this.compareSummaryOfEarns) * 100;
    this.earnPercentChange = calculatePercentChange(this.compareEarnsToDisplay, this.earnsToDisplay);
  }

  calculateLeadsCompare(): void {
    this.compareLeadsSummary =
      parseInt(this.campaignToCompare.leads, 10) +
      parseInt(this.campaignToCompare.leads_status_payment_waiting, 10) +
      parseInt(this.campaignToCompare.leads_status_wait, 10);
    if (this.title === 'ivr' || this.title === 'hidelink') {
      this.compareLeadsSummary = parseInt(this.campaignToCompare.leads, 10);
    }
    this.leadPercentChange = calculatePercentChange(this.compareLeadsSummary, this.leadsSummary);
  }

  open(modalDescription: string, modalTitle: string, modalType: string, configurationData?: any): void {
    this.shareOpenModalStatus.shareModalStatus(true);
    const modalRef = this.modalService.open(SingleCampaignModalComponent, {
      size: 'me'
    });
    modalRef.componentInstance.campaignData = this.campaign;
    modalRef.componentInstance.modalDescriptionName = modalDescription;
    modalRef.componentInstance.modalTitle = modalTitle;
    modalRef.componentInstance.modalType = modalType;
    if (modalType === 'edit_configuration') {
      modalRef.componentInstance.configurationData = configurationData;
    }
  }

  private setCompareCampaign = (title: string) => {
    if (title === 'countries') {
      const compareCampaign = this.statisticsCompareData.find(
        (compareCampaign: any) => compareCampaign[this.paramToCompare].id === this.campaign?.[this.paramToCompare as keyof ContentItemCampaign].id
      );

      return (this.campaignToCompare = compareCampaign ?? {});
    }

    if (title === 'stats_daily') {
      return (this.campaignToCompare = this.statisticsCompareData[this.index]);
    }

    return (this.campaignToCompare = this.statisticsCompareData.find(
      (compareCampaign: any) => compareCampaign[this.paramToCompare] === this.campaign?.[this.paramToCompare as keyof ContentItemCampaign]
    ));
  };
}
